import React from 'react';
import { useStyles } from './styles';
import { Box, Grid, Container, Typography } from '@mui/material';
import CustomButton from 'components/CustomButton';
import useIsMobile from 'hooks/useIsMobile';
import LazyLoad from 'react-lazyload';
import CheckBenefits from '../CheckBenefits';
import Testimonials from 'components/Testimonials';
import { flowTrialActive } from 'utils';
import { useAuthUser } from 'features/auth/AuthUserContext';
import { useRouter } from 'next/router';

export default function MainContent() {
  const classes = useStyles();
  const { register } = useAuthUser();
  const isMobile = useIsMobile();
  const textButtons = flowTrialActive ? 'Get Started Now' : 'Start creating now';
  const router = useRouter();

  return (
    <>
      <Box bgcolor="#F1F1F1">
        <Container>
          <LazyLoad offset={100}>
            <Box py={8}>
              <Grid container={true} spacing={2} justifyContent="center" alignItems="center">
                <Grid item={true} xs={12} md={6} align={!isMobile ? 'left' : 'center'}>
                  <img
                    src="/images/poster/poster-scientific-research-templates-icon.svg"
                    alt="scientific research template icon"
                    loading="lazy"
                    width="60"
                    height="64"
                  />

                  <Typography className={classes.primaryColor}>
                    <b>Unlimited use of 200+ scientifically designed templates</b>
                  </Typography>

                  <ul className={classes.listBenefits}>
                    <li>
                      Browse through an unlimited amount of professionally made templates. Never
                      start from a blank list again.
                    </li>
                    <li>
                      Stunning templates from cardiology, biology, chemistry & from 50+ scientific
                      fields.
                    </li>
                    <li>
                      Create your own project or choose from a variety of professionally made ones.
                    </li>
                  </ul>

                  <CustomButton onClick={() => router.push('/infographic-templates')}>
                    Browse templates
                  </CustomButton>
                </Grid>

                <Grid item={true} xs={12} md={6} align={!isMobile ? 'right' : 'center'}>
                  <img
                    src="/images/benefits/templates.webp"
                    alt="templates image"
                    loading="lazy"
                    width={!isMobile ? '491' : '350'}
                    height={!isMobile ? '523' : '372'}
                  />
                </Grid>
              </Grid>
            </Box>
          </LazyLoad>
        </Container>
      </Box>

      <Box bgcolor="#FFFFFF">
        <Container>
          <LazyLoad offset={100}>
            <Box py={8}>
              <Grid container={true} spacing={2} justifyContent="center" alignItems="center">
                <Grid item={true} xs={12} md={6} align={!isMobile ? 'left' : 'center'}>
                  <img
                    src="/images/createGraphicalAbstract/illustration-icon.webp"
                    alt="illustration icon"
                    loading="lazy"
                    width="51"
                    height="59"
                  />

                  <Typography className={classes.primaryColor}>
                    <b>Scientifically accurate illustrations and figures with no limits</b>
                  </Typography>

                  <ul className={classes.listBenefits}>
                    <li>
                      Access unlimited amount of scientific illustrations in cardiology, biology,
                      neurology & in 50+ scientific fields.
                    </li>
                    <li>
                      No watermarks in your illustrations anymore. Bring visuals to your research.
                    </li>
                    <li>Visualize any topic with fully editable scientific figures.</li>
                  </ul>

                  <CustomButton onClick={() => router.push('/illustrations')}>
                    Browse illustrations gallery
                  </CustomButton>
                </Grid>

                <Grid item={true} xs={12} md={6} align={!isMobile ? 'right' : 'center'}>
                  <img
                    src="/images/benefits/illustrations.webp"
                    alt="illustrations image"
                    loading="lazy"
                    width={!isMobile ? '494' : '350'}
                    height={!isMobile ? '384' : '272'}
                  />
                </Grid>
              </Grid>
            </Box>
          </LazyLoad>
        </Container>
      </Box>

      <Box bgcolor="#F1F1F1">
        <Container>
          <LazyLoad offset={100}>
            <Box py={8}>
              <Grid container={true} spacing={2} justifyContent="center" alignItems="center">
                <Grid item={true} xs={12} md={6} align={!isMobile ? 'left' : 'center'}>
                  <img
                    src="/images/unlimited-scientific-illustrations-icon.svg"
                    alt="unlimited scientific illustrations icon"
                    loading="lazy"
                    width="52"
                    height="63"
                  />

                  <Typography className={classes.primaryColor}>
                    <b>On-demand illustrations & templates</b>
                  </Typography>

                  <ul className={classes.listBenefits}>
                    <li>Receive free personalized templates exclusively for your brand.</li>
                    <li>
                      Need a professional illustration? Our team will create one for you. Stand out
                      at your next conference.
                    </li>
                    <li>100% confidentiality for all custom made illustrations.</li>
                    <li>Request up to 50 unique illustrations per month.</li>
                  </ul>

                  <CustomButton onClick={register}>{textButtons}</CustomButton>
                </Grid>

                <Grid item={true} xs={12} md={6} align={!isMobile ? 'right' : 'center'}>
                  <img
                    src="/images/benefits/ondemand-illustrations.webp"
                    alt="ondemand illustrations"
                    loading="lazy"
                    width={!isMobile ? '477' : '350'}
                    height={!isMobile ? '468' : '343'}
                  />
                </Grid>
              </Grid>
            </Box>
          </LazyLoad>
        </Container>
      </Box>

      <Box bgcolor="#FFFFFF">
        <Container>
          <LazyLoad offset={100}>
            <Box py={8}>
              <Grid container={true} spacing={2} justifyContent="center" alignItems="center">
                <Grid item={true} xs={12} md={6} align={!isMobile ? 'left' : 'center'}>
                  <img
                    src="/images/benefits/team-icon.svg"
                    alt="team icon"
                    loading="lazy"
                    width="98"
                    height="48"
                  />

                  <Typography className={classes.primaryColor}>
                    <b>Team collaboration with no effort</b>
                  </Typography>

                  <ul className={classes.listBenefits}>
                    <li>Bring up to 10 team members along & work on projects simultaneously.</li>
                    <li>Share your content amongst team members with zero boundaries.</li>
                    <li>Freely add & remove team members. Have full control over your team.</li>
                    <li>
                      Work in teams & receive discounts for graphical abstracts & on demand visual
                      abstracts.
                    </li>
                  </ul>

                  <CustomButton onClick={register}>{textButtons}</CustomButton>
                </Grid>

                <Grid item={true} xs={12} md={6} align={!isMobile ? 'right' : 'center'}>
                  <img
                    src="/images/benefits/team-collaboration.webp"
                    alt="team collaboration image"
                    loading="lazy"
                    width={!isMobile ? '475' : '350'}
                    height={!isMobile ? '474' : '349'}
                  />
                </Grid>
              </Grid>
            </Box>
          </LazyLoad>
        </Container>
      </Box>

      <Box bgcolor="#F1F1F1">
        <Container>
          <LazyLoad offset={100}>
            <Box py={8}>
              <Grid container={true} spacing={2} justifyContent="center" alignItems="center">
                <Grid item={true} xs={12} md={6} align={!isMobile ? 'left' : 'center'}>
                  <img
                    src="/images/benefits/support-icon.svg"
                    alt="support icon"
                    loading="lazy"
                    width="90"
                    height="54"
                  />

                  <Typography className={classes.primaryColor}>
                    <b>Professional support from Mind The Graph</b>
                  </Typography>

                  <ul className={classes.listBenefits}>
                    <li>
                      Design slides with free support from our design team. You don't need to be an
                      artist to have a stunning presentation.
                    </li>
                    <li>Always get tips & help from our Infography & design team.</li>
                    <li>
                      You have questions, we have answers. Mind The Graph's support team is always
                      ready to help.
                    </li>
                  </ul>

                  <CustomButton onClick={register}>{textButtons}</CustomButton>
                </Grid>

                <Grid item={true} xs={12} md={6} align={!isMobile ? 'right' : 'center'}>
                  <img
                    src="/images/benefits/graphical-abstract-maker.webp"
                    alt="graphical abstract maker image"
                    loading="lazy"
                    width={!isMobile ? '475' : '350'}
                    height={!isMobile ? '433' : 319}
                  />
                </Grid>
              </Grid>
            </Box>
          </LazyLoad>
        </Container>
      </Box>

      <LazyLoad offset={100}>
        <CheckBenefits />
      </LazyLoad>

      <Box pt={10} pb={8} bgcolor="#F1F1F1">
        <Container>
          <LazyLoad offset={100}>
            <Testimonials />
          </LazyLoad>
        </Container>
      </Box>
    </>
  );
}
